import { memo, useEffect, useRef, useState, useCallback, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  selectCurrentChannel,
  selectPlayerType,
  selectAllChannels,
  setCurrentChannel,
  setPlayerType,
  selectChannels,
  setChannels,
  selectSelectedCategory,
} from "@app/channels/channelsSlice";
import { useTranslation } from "react-i18next";
import {
  setShowPreviewImages,
  selectShowPreviewImages,
  selectIsPaused,
  selectCurrentArchive,
  setCurrentArchive,
  setPaused,
  setArchives,
  selectArchives,
} from "@app/player/playerSlice";
import { channelInfo, getArchiveUrl, getEpgList } from "@server/requests";
import {
  formatDate,
  formatTime,
  setUrlArchive,
  debounce,
  setPlayerPosition,
} from "@utils/util";
import {
  selectCtrl,
  setCtrl,
  setIsCategoriesOpen,
  setLastActiveIndex,
} from "@app/global";
import {
  addBreadcrumb,
  captureError,
  captureMessage,
} from "../../config/sentry";

import useKeydown from "@hooks/useKeydown";

import LOCAL_STORAGE from "@utils/localStorage";

import LiveIcon from "./components/LiveIcon";
import InfoLiveControl from "./components/InfoLiveControl";
import ArchiveButtons from "./components/ArchiveButtons";
import FavActiveSvg from "@assets/icons/FavActiveSvg";
import SvgBackward from "@assets/images/live/backward";
import SvgCalendar from "@assets/icons/SvgCalendar";
import SvgFastForward from "@assets/images/player/SvgForward";
import SvgFastBackward from "@assets/images/player/SvgRewind";

import "./styles/LiveControl.scss";
import { addLiveFavorite, removeLiveFavorite } from "../../server/requests";
import { InoButton, InoPlayerProgress } from "@ino-ui/tv";
import PreviewImages from "./components/PreviewImages";
import ProgressBar from "@components/player/components/ProgressBar";
import Calendar from "./components/Calendar";

let hideControlsTimer = null;

const formatPlaybackTime = (timestamp) => {
  const date = new Date(timestamp);
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const formattedHours = hours < 10 ? `0${hours}` : hours;
  const formattedMinutes = minutes < 10 ? `0${minutes}` : minutes;

  return `${formattedHours}:${formattedMinutes}`;
};

export default memo(function LiveControls({
  setPipMode,
  setUrl,
  showProtected,
  url,
  isArchiveLoaded,
  refUrlLive,
  secCurrentTime,
  refVideo,
  play,
  pause,
  isArchivedLoaded,
  cTime,
  commitSeek,
  duration,
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const allChannels = useSelector(selectAllChannels);
  const currentChannel = useSelector(selectCurrentChannel);
  const playerType = useSelector(selectPlayerType);
  const showPreviewImages = useSelector(selectShowPreviewImages);
  const isPaused = useSelector(selectIsPaused);
  const ctrl = useSelector(selectCtrl);
  const categoryChannels = useSelector(selectChannels);
  const selectedCategory = useSelector(selectSelectedCategory);
  const progressBarRef = useRef(null);

  const refNextChannel = useRef(null);
  const refPrevChannel = useRef(null);
  const timeOutNumber = useRef(null);
  const refVal = useRef(null);
  const targetChannelRef = useRef(null);

  const [number, setNumber] = useState("");
  const [active, setActive] = useState(0);
  const [hideControls, setHideControls] = useState(false);
  const [time, setTime] = useState(new Date().getTime());

  const channelChangeTimeout = useRef(null);

  const [displayChannel, setDisplayChannel] = useState(null);

  const currentArchive = useSelector(selectCurrentArchive);
  const archives = useSelector(selectArchives);

  const [showCalendar, setShowCalendar] = useState(false);
  const [calendarActive, setCalendarActive] = useState(false);

  const [seekTime, setSeekTime] = useState(null);
  const seekDebounceRef = useRef(null);
  const wasPlayingRef = useRef(false);

  const isChannelChangingRef = useRef(false);

  const debouncedGetChannelInfo = useCallback(
    debounce((id) => {
      if (isChannelChangingRef.current) {
        addBreadcrumb("Channel change blocked - already changing", "channel", {
          targetId: id,
        });
        captureMessage("Channel change blocked", "warning", {
          targetId: id,
          currentChannelId: currentChannel?.id,
        });
        return;
      }

      addBreadcrumb("Channel change started", "channel", { targetId: id });
      isChannelChangingRef.current = true;
      getChannelInfo(id)
        .catch((err) => {
          captureError(err, { context: "getChannelInfo failed", targetId: id });
        })
        .finally(() => {
          setTimeout(() => {
            isChannelChangingRef.current = false;
            addBreadcrumb("Channel change lock released", "channel", {
              targetId: id,
            });
          }, 500);
        });
    }, 300),
    [currentChannel],
  );

  const findChannel = () => {
    if (allChannels && !allChannels.length) return;

    // First try to find the current channel index
    let currentChannelIndex = -1;
    if (currentChannel) {
      currentChannelIndex = allChannels.findIndex(
        (i) => i.id === currentChannel.id,
      );
    }

    // If we found the current channel, calculate next and previous
    if (currentChannelIndex !== -1) {
      // Find next non-protected channel
      let nextIdx = currentChannelIndex;
      do {
        nextIdx = (nextIdx + 1) % allChannels.length;
      } while (
        nextIdx !== currentChannelIndex &&
        allChannels[nextIdx].is_protected
      );

      if (nextIdx !== currentChannelIndex) {
        refNextChannel.current = allChannels[nextIdx];
      }

      // Find previous non-protected channel
      let prevIdx = currentChannelIndex;
      do {
        prevIdx = prevIdx === 0 ? allChannels.length - 1 : prevIdx - 1;
      } while (
        prevIdx !== currentChannelIndex &&
        allChannels[prevIdx].is_protected
      );

      if (prevIdx !== currentChannelIndex) {
        refPrevChannel.current = allChannels[prevIdx];
      }
    }
    // If we're just starting or have no current channel
    else {
      // Try to load a channel if we don't have one
      if (!currentArchive) {
        let channel = localStorage.getItem("last_channel_id");
        if (!channel) channel = allChannels[0].id;
        debouncedGetChannelInfo(+channel);
      }
    }
  };

  const getChannelInfo = async (id) => {
    try {
      addBreadcrumb("Fetching channel info", "channel", { channelId: id });
      const response = await channelInfo({ id: id });
      const parsedResponse = JSON.parse(response);
      const { error, message } = parsedResponse;

      if (error) {
        addBreadcrumb("Channel info error", "channel", {
          channelId: id,
          error,
        });
        captureMessage("Channel info API error", "error", {
          channelId: id,
          error,
        });
      } else {
        let _url = message.url;

        if (message.stream_type === "internal" && LOCAL_STORAGE.TOKEN.GET()) {
          _url += "?token=" + LOCAL_STORAGE.TOKEN.GET();
        }

        if (!message?.is_protected) {
          LOCAL_STORAGE.LAST_CHANNEL_ID.SET(id);
        }
        dispatch(setCurrentChannel(message));

        if (message.is_protected) {
          showProtected();
        } else {
          addBreadcrumb("Channel URL set", "channel", {
            channelId: id,
            hasUrl: !!_url,
          });
          setUrl(_url);
          if (playerType === "timeshift") {
            refVideo.current.currentTime = 0;
          }
        }
      }
    } catch (err) {
      captureError(err, { context: "getChannelInfo exception", channelId: id });
      throw err;
    }
  };

  const numberChangeChannel = (num) => {
    let _number = Number(number + num.toString());

    if ((number + num.toString()).length <= 4) {
      setNumber(_number);

      if (timeOutNumber.current) {
        clearTimeout(timeOutNumber.current);
      }

      timeOutNumber.current = setTimeout(() => {
        findChannelByNumber(_number);
        setNumber("");
      }, 3000);
    }
  };

  const findChannelByNumber = (num) => {
    if (isChannelChangingRef.current) return;

    let _channel = null;
    for (let i = 0; i < allChannels.length; i++) {
      if (allChannels[i].position === num) {
        debouncedGetChannelInfo(allChannels[i].id);
        _channel = allChannels[i];
        break;
      } else if (
        num > allChannels[i].position &&
        allChannels[i + 1] &&
        num < allChannels[i + 1].position
      ) {
        _channel = allChannels[i];
        break;
      }
    }

    if (!_channel) _channel = allChannels[allChannels.length - 1];

    debouncedGetChannelInfo(_channel.id);
  };

  const getTimeshiftUrlHandler = async ({ id }) => {
    try {
      const response = await getArchiveUrl({ id, type: "timeshift" });
      const parsedResponse = JSON.parse(response);
      const { error, message } = parsedResponse;
      if (error) return null;
      return message.url;
    } catch (error) {
      console.log(error, "getTimeshiftUrlHandler error");
      return null;
    }
  };

  const setUrlTimeshift = async () => {
    if (currentChannel.url) {
      if (playerType === "live") {
        refUrlLive.current = url;
      }

      // let _url =
      //   currentChannel.url +
      //   "/timeshift/" +
      //   currentChannel.id +
      //   "/index.m3u8" +
      //   "?utc=" +
      //   Math.floor(Date.now() / 1000);

      const timeshiftUrl = await getTimeshiftUrlHandler({
        id: currentChannel.id,
      });

      setUrl(timeshiftUrl);
    }
  };

  const toggleFavorite = async (isAdd) => {
    try {
      if (isAdd) {
        dispatch(setCurrentChannel({ ...currentChannel, favorite: true }));
        const res = await addLiveFavorite({ channel_id: currentChannel.id });

        const categoryChannelsClone = JSON.parse(
          JSON.stringify(categoryChannels),
        );

        if (categoryChannelsClone) {
          if (!categoryChannelsClone.favorites) {
            categoryChannelsClone.favorites = {
              content: [],
              total: 0,
            };
          }
          const filteredCatChannels = [
            ...categoryChannelsClone.favorites.content,
            currentChannel,
          ];

          categoryChannelsClone.favorites.content = filteredCatChannels;
          categoryChannelsClone.favorites.total = filteredCatChannels.length;

          dispatch(setChannels(categoryChannelsClone));
        }

        const parsedRes = JSON.parse(res);
        if (parsedRes.error) {
          dispatch(setCurrentChannel({ ...currentChannel, favorite: false }));
        }
      } else {
        dispatch(setCurrentChannel({ ...currentChannel, favorite: false }));
        const res = await removeLiveFavorite({ channel_id: currentChannel.id });
        const categoryChannelsClone = JSON.parse(
          JSON.stringify(categoryChannels),
        );

        if (categoryChannelsClone && categoryChannelsClone.favorites) {
          const filteredCatChannels =
            categoryChannelsClone.favorites.content.filter(
              (channel) => channel.id !== currentChannel.id,
            );

          categoryChannelsClone.favorites.content = filteredCatChannels;
          categoryChannelsClone.favorites.total = filteredCatChannels.length;

          dispatch(setChannels(categoryChannelsClone));
        }

        const parsedRes = JSON.parse(res);
        if (parsedRes.error) {
          dispatch(setCurrentChannel({ ...currentChannel, favorite: true }));
        }
      }
    } catch (err) {
      dispatch(setCurrentChannel({ ...currentChannel, favorite: !isAdd }));
    }
  };

  const handleArchiveAction = (index) => {
    if (playerType === "live") {
      setUrlTimeshift();
      dispatch(setPlayerType("timeshift"));
      return;
    }

    switch (index) {
      case 0:
        handleSeek("rewind", 600);
        break;
      case 1:
        handleSeek("rewind", 300);
        break;
      case 2:
        handleSeek("rewind", 60);
        break;
      case 3:
        if (isPaused) play();
        else pause();
        break;
      case 4:
        handleSeek("forward", 60);
        break;
      case 5:
        handleSeek("forward", 300);
        break;
      case 6:
        handleSeek("forward", 600);
        break;
    }
  };

  useEffect(() => {
    const interval = setInterval(() => {
      setTime(new Date().getTime());
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!currentChannel?.id_epg) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await getEpgList({
          date: "-1,1",
          id: currentChannel.id_epg,
        });
        const parsedRes = JSON.parse(res);
        const { message, error } = parsedRes;
        if (cancelled || error || !message?.length) return;
        dispatch(setArchives([...message]));
      } catch {
        return;
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currentChannel?.id, currentChannel?.id_epg, dispatch]);

  const liveEpgTitle = useMemo(() => {
    const slot = archives.find(
      (e) => e.start_ut * 1000 < time && e.stop_ut * 1000 > time,
    );
    return slot?.title ?? null;
  }, [archives, time]);

  useEffect(() => {
    return () => {
      // dispatch(setShowPreviewImages(false));
      if (!isPaused) secCurrentTime.current = 0;
    };
  }, []);

  const showControl = () => {
    if (hideControls) setHideControls(false);

    clearTimeout(hideControlsTimer);

    hideControlsTimer = setTimeout(() => {
      setHideControls(true);
    }, 5000);
  };

  useEffect(() => {
    if (allChannels && !allChannels.length) return;
    findChannel();
    showControl();
  }, [currentChannel, allChannels]);

  const nextChannel = () => {
    showControl();
    if (isChannelChangingRef.current) {
      addBreadcrumb("Next channel blocked - already changing", "channel", {});
      return;
    }

    if (refNextChannel.current) {
      addBreadcrumb("Next channel triggered", "channel", {
        nextChannelId: refNextChannel.current.id,
      });
      if (channelChangeTimeout.current) {
        clearTimeout(channelChangeTimeout.current);
      }

      targetChannelRef.current = refNextChannel.current;

      setDisplayChannel(refNextChannel.current.position);

      dispatch(setCurrentChannel(refNextChannel.current));
      dispatch(setPaused(false));

      setActive(0);

      channelChangeTimeout.current = setTimeout(() => {
        setDisplayChannel(null);
        if (targetChannelRef.current) {
          getChannelInfo(targetChannelRef.current.id);
        }
      }, 800);
    }
  };

  const prevChannel = () => {
    showControl();
    if (hideControls || isChannelChangingRef.current) {
      addBreadcrumb("Prev channel blocked", "channel", {
        hideControls,
        isChanging: isChannelChangingRef.current,
      });
      return;
    }

    if (refPrevChannel.current) {
      addBreadcrumb("Prev channel triggered", "channel", {
        prevChannelId: refPrevChannel.current.id,
      });
      if (channelChangeTimeout.current) {
        clearTimeout(channelChangeTimeout.current);
      }

      targetChannelRef.current = refPrevChannel.current;

      setDisplayChannel(refPrevChannel.current.position);

      dispatch(setCurrentChannel(refPrevChannel.current));
      dispatch(setPaused(false));

      setActive(0);

      channelChangeTimeout.current = setTimeout(() => {
        setDisplayChannel(null);
        if (targetChannelRef.current) {
          getChannelInfo(targetChannelRef.current.id);
        }
      }, 800);
    }
  };

  const findCurrentIndex = useCallback(() => {
    return categoryChannels[selectedCategory]?.content?.findIndex((item) => {
      return item.id === currentChannel?.id;
    });
  }, [categoryChannels, selectedCategory, currentChannel]);

  useKeydown({
    isActive:
      showPreviewImages &&
      ctrl !== "protected" &&
      ctrl !== "archiveBtns" &&
      !showCalendar,

    back: () => {
      if (!window.Android) {
        if (!isPaused) refVideo.current.play();
      }
      dispatch(setShowPreviewImages(false));
      setSeekTime(null);
    },

    ok: () => {
      if (!isArchivedLoaded) return;
      if (seekTime !== null) {
        commitSeek(seekTime);
        if (!isPaused) play();
      }
      dispatch(setShowPreviewImages(false));
    },
  });

  const getArchiveUrlHandler = async ({ id, start, end }) => {
    try {
      const response = await getArchiveUrl({
        id,
        start: +start,
        end: +end,
        type: "archive",
      });
      const parsedResponse = JSON.parse(response);
      const { error, message } = parsedResponse;
      if (error) {
        return null;
      }
      return message.url;
    } catch (error) {
      console.log(error, "error");
      return null;
    }
  };

  const onBackHandler = () => {
    const currentIndex = findCurrentIndex();
    dispatch(setLastActiveIndex(currentIndex));

    if (number) {
      if (timeOutNumber.current) {
        clearTimeout(timeOutNumber.current);
      }
      setNumber("");
      return;
    }
    dispatch(setIsCategoriesOpen(true));
    setPipMode(true);
    setPlayerPosition(true);
  };

  useKeydown({
    isActive:
      playerType === "live" &&
      ctrl !== "protected" &&
      ctrl !== "archiveBtns" &&
      !showCalendar,

    fast_prev: () => {
      showControl();
      if (hideControls) return;
      setUrlTimeshift();
      dispatch(setPlayerType("timeshift"));
    },

    fast_next: () => {
      showControl();
      if (hideControls) return;
      setUrlTimeshift();
      dispatch(setPlayerType("timeshift"));
    },

    number: (e) => {
      showControl();
      // if (hideControls) return;
      numberChangeChannel(e.key);
    },

    left: () => {
      showControl();
      if (hideControls) return;
      if (currentChannel?.has_archive) {
        if (active < 1) return;
        if (active === 6) {
          // setActive(5);
          dispatch(setCtrl("archiveBtns"));
        } else if (active === 5) {
          setActive(4);
        } else if (active === 4) {
          setActive(3);
        } else if (active === 3) {
          setActive(2);
        } else if (active === 2) {
          setActive(1);
        } else if (active === 1) {
          setActive(0);
        }
      } else {
        if (active === 1) {
          setActive(0);
        } else if (ctrl === "archiveBtns") {
          setActive(1);
          dispatch(setCtrl("live"));
        }
      }
    },

    right: () => {
      if (hideControls) return showControl();

      if (active === 0) {
        setActive(1);
        return;
      }

      if (active === 1) {
        if (!currentChannel?.has_archive) {
          if (playerType === "live") {
            setActive(2);
            dispatch(setCtrl("archiveBtns"));
          }
        } else {
          setActive(2);
        }
        return;
      }

      if (active === 2) {
        if (!currentChannel?.has_archive) {
          return;
        }
        setActive(3);
        return;
      }

      if (active === 3) {
        setActive(4);
        return;
      }

      if (active === 4) {
        // setActive(5);
        dispatch(setCtrl("archiveBtns"));

        return;
      }

      // if (active === 5) {
      //     dispatch(setCtrl("archiveBtns"));
      // }
    },

    up: () => {
      showControl();
      if (hideControls) return showControl();
      if (number) {
        if (timeOutNumber.current) {
          clearTimeout(timeOutNumber.current);
        }
        setNumber("");
        return;
      }
      nextChannel();
    },

    down: () => {
      showControl();
      if (hideControls) return showControl();
      if (number) {
        if (timeOutNumber.current) {
          clearTimeout(timeOutNumber.current);
        }
        setNumber("");
        return;
      }
      prevChannel();
    },

    back: () => {
      onBackHandler();
    },

    ok: () => {
      showControl();
      if (hideControls || isChannelChangingRef.current) return;

      if (number) {
        findChannelByNumber(Number(number));
        setNumber("");
        return;
      }

      if (active === 0) {
        setPipMode(true);
        setPlayerPosition(true);
      } else if (active === 1) {
        toggleFavorite(!currentChannel.favorite);
      } else if (active === 2) {
        setShowCalendar(true);
        dispatch(setCtrl(""));
        setHideControls(true);
      } else if (active === 3 || active === 4) {
        if (playerType === "archive") {
          dispatch(setShowPreviewImages(true));
        }

        if (active === 3) {
          handleSeek("rewind", 30);
        } else if (active === 4) {
          handleSeek("forward", 30);
        }

        dispatch(setCtrl("archiveBtns"));
      } else {
        if (hideControls) return showControl();
      }
    },
  });

  useKeydown({
    isActive:
      (playerType === "timeshift" || playerType === "archive") &&
      ctrl !== "previewImage" &&
      ctrl !== "protected" &&
      !showCalendar &&
      ctrl !== "archiveBtns",

    number: (e) => {
      showControl();
      numberChangeChannel(e.key);
    },

    fast_prev: () => {
      showControl();
      if (hideControls) return;
      if (playerType === "archive") {
        dispatch(setShowPreviewImages(true));
      }
      handleSeek("rewind", 10);
    },

    fast_next: () => {
      showControl();
      if (hideControls) return;
      if (playerType === "archive") {
        dispatch(setShowPreviewImages(true));
      }
      handleSeek("forward", 10);
    },

    pause: () => {
      secCurrentTime.current = 0;
      if (isPaused) play();
      else pause();
    },

    play: () => {
      secCurrentTime.current = 0;
      if (isPaused) play();
      else pause();
    },

    left: () => {
      showControl();
      if (hideControls) return;
      if (currentChannel?.has_archive) {
        if (active < 1) return;
        if (active === 6) {
          setActive(5);
          dispatch(setCtrl("archiveBtns"));
        } else if (active === 5) {
          setActive(4);
        } else if (active === 4) {
          setActive(3);
        } else if (active === 3) {
          setActive(2);
        } else if (active === 2) {
          setActive(1);
        } else if (active === 1) {
          setActive(0);
        }
      } else {
        if (active === 1) {
          setActive(0);
        } else if (ctrl === "archiveBtns") {
          setActive(1);
          dispatch(setCtrl("live"));
        }
      }
    },

    right: () => {
      if (hideControls) return showControl();

      if (active === 0) {
        setActive(1);
        return;
      }

      if (active === 1) {
        if (!currentChannel?.has_archive) {
          if (playerType === "live") {
            setActive(6);
            dispatch(setCtrl("archiveBtns"));
          }
        } else {
          setActive(2);
        }
      } else if (active === 2) {
        setActive(3);
      } else if (active === 3) {
        setActive(4);
      } else if (active === 4) {
        dispatch(setCtrl("archiveBtns"));
      }
    },

    up: () => {
      showControl();
      if (hideControls) return showControl();
      if (refNextChannel.current) {
        dispatch(setPlayerType("live"));
        dispatch(setPaused(false));
        getChannelInfo(refNextChannel.current.id);
      }
    },

    down: () => {
      showControl();
      if (hideControls) return;
      if (refPrevChannel.current && active === 0) {
        dispatch(setPlayerType("live"));
        dispatch(setPaused(false));
        getChannelInfo(refPrevChannel.current.id);
      }
    },

    back: () => {
      showControl();
      if (hideControls) return;
      dispatch(setIsCategoriesOpen(true));
      setPipMode(true);
      setPlayerPosition(true);
    },

    ok: () => {
      showControl();
      if (hideControls || isChannelChangingRef.current) return;

      if (active === 0) {
        dispatch(setIsCategoriesOpen(true));
        setPipMode(true);
        setPlayerPosition(true);
      } else if (active === 1) {
        toggleFavorite(!currentChannel.favorite);
      } else if (active === 2) {
        if (currentChannel) {
          debouncedGetChannelInfo(currentChannel.id);
        }
        dispatch(setPlayerType("live"));
      } else if (active === 3 || active === 4) {
        if (playerType === "archive") {
          dispatch(setShowPreviewImages(true));
        }

        if (active === 3) {
          handleSeek("rewind", 10);
        } else if (active === 4) {
          handleSeek("forward", 10);
        }

        dispatch(setCtrl("archiveBtns"));
      } else {
        if (hideControls) return showControl();
      }
    },
  });

  useEffect(() => {
    return () => {
      if (channelChangeTimeout.current) {
        clearTimeout(channelChangeTimeout.current);
      }
      if (seekDebounceRef.current) {
        clearTimeout(seekDebounceRef.current);
      }
      if (timeOutNumber.current) {
        clearTimeout(timeOutNumber.current);
      }
    };
  }, []);

  const handleSeek = (direction, seconds = 10) => {
    const currentPos = seekTime !== null ? seekTime : cTime;
    const safeDuration = duration > 0 ? duration : Infinity;
    const newTime =
      direction === "forward"
        ? Math.min(currentPos + seconds, safeDuration)
        : Math.max(currentPos - seconds, 0);

    if (seekTime === null) {
      wasPlayingRef.current = !isPaused;
    }

    pause();

    setSeekTime(newTime);

    clearTimeout(seekDebounceRef.current);
    seekDebounceRef.current = setTimeout(() => {
      commitSeek(newTime);
      setTimeout(() => {
        if (wasPlayingRef.current) play();
      }, 500);
    }, 1500);
  };

  useEffect(() => {
    if (seekTime !== null && Math.abs(cTime - seekTime) < 2) {
      setSeekTime(null);
    }
  }, [cTime, seekTime]);

  const progressOverlayTitle =
    playerType === "archive" && currentArchive?.title
      ? currentArchive.title
      : (playerType === "live" || playerType === "timeshift") && liveEpgTitle
        ? liveEpgTitle
        : null;

  return (
    <>
      {number || displayChannel ? (
        <p className="num-change-channel">{number || displayChannel}</p>
      ) : null}
      <div className={`live-control${hideControls ? " hide" : ""}`}>
        <InfoLiveControl
          playerType={playerType}
          currentChannel={currentChannel}
          active={active}
        />
        <div className="progres-field">
          {progressOverlayTitle ? (
            <div className="archive-program-info">
              <span className="archive-program-title">
                {progressOverlayTitle}
              </span>
            </div>
          ) : null}
          {playerType === "live" ? (
            <ProgressBar
              value={seekTime !== null ? seekTime : cTime}
              duration={duration}
              isLive={true}
            />
          ) : (
            <>
              <div className="live-test-wrapper">
                {playerType === "timeshift" ? (
                  <div className="timeshift-progress__time_current">
                    {formatPlaybackTime(
                      time -
                        (duration || 0) * 1000 +
                        ((seekTime !== null ? seekTime : cTime) || 0) * 1000,
                    )}
                  </div>
                ) : (
                  <div className={`progress__time_current ${playerType}`}>
                    {formatPlaybackTime(
                      (currentArchive?.start_ut +
                        (seekTime !== null ? seekTime : cTime)) *
                        1000,
                    )}
                  </div>
                )}
              </div>
              <div className="progress__time_current">
                {playerType === "timeshift" ? (
                  <>{formatPlaybackTime(time)}</>
                ) : (
                  <>{formatPlaybackTime(currentArchive?.stop_ut * 1000)}</>
                )}
              </div>

              <div ref={progressBarRef}>
                <ProgressBar
                  value={seekTime !== null ? seekTime : cTime}
                  duration={duration}
                  classNames={playerType}
                />
              </div>
            </>
          )}

          {playerType === "archive" ? (
            <PreviewImages
              showPreviewImages={showPreviewImages}
              currentChannel={currentChannel}
              currentArchive={currentArchive}
              currentStep={Math.floor(
                (seekTime !== null ? seekTime : cTime) / 10,
              )}
              totalSteps={Math.floor(duration / 10)}
            />
          ) : null}

          <ArchiveButtons
            onBackHandler={onBackHandler}
            play={play}
            pause={pause}
            isPaused={isPaused}
            changeChannel={(direction) => {
              dispatch(setCtrl("live"));
              dispatch(setPlayerType("live"));
              dispatch(setPaused(false));
              if (direction === "next") {
                nextChannel();
              } else {
                prevChannel();
              }
            }}
            type={playerType}
            changeChangelWithNumber={(num) => {
              numberChangeChannel(num);
            }}
            active={ctrl === "archiveBtns"}
            setActive={(isLeft) => {
              if (isLeft) {
                if (currentChannel?.has_archive) {
                  setActive(4);
                } else {
                  setActive(1);
                }
              } else {
                setActive(6);
              }
              dispatch(setCtrl("live"));
            }}
            actionHandler={(index) => {
              handleArchiveAction(index);
            }}
            hasArchive={currentChannel?.has_archive}
            showControlHandler={() => {
              showControl();
            }}
            hideControls={hideControls}
            onLiveHandler={() => {
              dispatch(setPlayerType("live"));
              if (playerType === "timeshift") {
                // Always access the URL via the url property
                if (refUrlLive.current && refUrlLive.current.url) {
                  setUrl(refUrlLive.current.url);
                } else if (typeof refUrlLive.current === "string") {
                  setUrl(refUrlLive.current);
                } else {
                  // Fallback to getting channel info if no stored URL
                  const lastChannelId = LOCAL_STORAGE.LAST_CHANNEL_ID.GET();
                  getChannelInfo(lastChannelId);
                }
              } else {
                const lastChannelId = LOCAL_STORAGE.LAST_CHANNEL_ID.GET();
                getChannelInfo(lastChannelId);
              }
            }}
            isArchiveLoaded={isArchiveLoaded}
          />
          <LiveIcon type={playerType} isActive={false} />
        </div>
        <div className="live-control_actions_wrapper">
          <div className={"live-fav_wrapper"}>
            {currentChannel?.favorite ? (
              <InoButton
                classNames="rounded-btn"
                size="small"
                isActive={active == 1 && ctrl !== "archiveBtns"}
                data-testid="favorite-button"
              >
                <FavActiveSvg
                  onClick={() => toggleFavorite(false)}
                  isFill={true}
                />
              </InoButton>
            ) : (
              <InoButton
                classNames="rounded-btn"
                size="small"
                isActive={
                  active == 1 &&
                  ctrl !== "archiveBtns" &&
                  ctrl !== "previewImage"
                }
              >
                <FavActiveSvg
                  onClick={() => toggleFavorite(true)}
                  isFill={false}
                />
              </InoButton>
            )}

            {currentChannel?.has_archive && (
              <>
                <InoButton
                  classNames="rounded-btn calendar-btn"
                  size="small"
                  isActive={
                    active === 2 && ctrl !== "archiveBtns" && !showCalendar
                  }
                  onClick={() => {
                    if (hideControls) return showControl();

                    setShowCalendar(true);
                    dispatch(setCtrl(""));
                    setHideControls(true);
                  }}
                  data-testid="calendar-button"
                >
                  <SvgCalendar />
                </InoButton>
                <InoButton
                  classNames="rounded-btn fast-control-btn"
                  size="small"
                  isActive={active === 3 && ctrl !== "archiveBtns"}
                  onClick={() => {
                    if (hideControls) return showControl();
                    showControl();
                    if (playerType === "live") {
                      setUrlTimeshift();
                      dispatch(setPlayerType("timeshift"));
                      return;
                    }
                    if (playerType === "archive") {
                      dispatch(setShowPreviewImages(true));
                    }
                    handleSeek("rewind", 10);
                  }}
                  data-testid="rewind-button"
                >
                  <SvgFastBackward />
                </InoButton>
                <InoButton
                  classNames="rounded-btn fast-control-btn"
                  size="small"
                  isActive={active === 4 && ctrl !== "archiveBtns"}
                  onClick={() => {
                    if (hideControls) return showControl();
                    showControl();
                    if (playerType === "live") {
                      setUrlTimeshift();
                      dispatch(setPlayerType("timeshift"));
                      return;
                    }
                    if (playerType === "archive") {
                      dispatch(setShowPreviewImages(true));
                    }
                    handleSeek("forward", 10);
                  }}
                  data-testid="forward-button"
                >
                  <SvgFastForward />
                </InoButton>
              </>
            )}
          </div>
          {currentChannel?.has_archive ? (
            <InoButton
              size="small"
              classNames="timeshift-btn"
              isActive={active === 6 && ctrl !== "archiveBtns"}
              onClick={() => {
                if (hideControls) return showControl();
                if (playerType === "archive" || playerType === "timeshift") {
                  dispatch(setPlayerType("live"));
                  if (playerType === "timeshift") {
                    // Always access the URL via the url property
                    if (refUrlLive.current && refUrlLive.current.url) {
                      setUrl(refUrlLive.current.url);
                    } else if (typeof refUrlLive.current === "string") {
                      setUrl(refUrlLive.current);
                    } else {
                      const lastChannelId = LOCAL_STORAGE.LAST_CHANNEL_ID.GET();
                      getChannelInfo(lastChannelId);
                    }
                  } else {
                    const lastChannelId = LOCAL_STORAGE.LAST_CHANNEL_ID.GET();
                    getChannelInfo(lastChannelId);
                  }
                } else {
                  // Store the current URL before switching to timeshift
                  refUrlLive.current = url; // Store URL directly as string
                  setUrlTimeshift();
                  dispatch(setPlayerType("timeshift"));
                }
              }}
              data-testid="timeshift-button"
            >
              {playerType === "archive" || playerType === "timeshift" ? (
                <p>Live</p>
              ) : (
                <SvgBackward />
              )}
            </InoButton>
          ) : null}
        </div>
      </div>
      <Calendar
        isVisible={showCalendar}
        onClose={() => {
          setShowCalendar(false);
          showControl();
          dispatch(setCtrl("live"));
        }}
        onDateSelect={async (timestamp) => {
          if (currentChannel?.has_archive) {
            const findEpgForTimestamp = (items) =>
              items?.find(
                (e) => timestamp >= e.start_ut && timestamp < e.stop_ut,
              );

            let epgItem = findEpgForTimestamp(archives);
            if (!epgItem && currentChannel?.id_epg) {
              try {
                const res = await getEpgList({
                  date: "-1,1",
                  id: currentChannel.id_epg,
                });
                const parsedRes = JSON.parse(res);
                const { message, error } = parsedRes;
                if (!error && message?.length) {
                  dispatch(setArchives([...message]));
                  epgItem = findEpgForTimestamp(message);
                }
              } catch {
                epgItem = undefined;
              }
            }

            const archive = {
              start_ut: timestamp,
              stop_ut: timestamp + 3600 * 3,
              channelId: currentChannel.id,
              ...(epgItem?.title ? { title: epgItem.title } : {}),
            };

            const archiveUrl = await getArchiveUrlHandler({
              id: currentChannel.id,
              start: timestamp,
              end: timestamp + 3600 * 3,
            });

            if (!archiveUrl) {
              setShowCalendar(false);
              dispatch(setCtrl("live"));
              showControl();
              return;
            }

            if (playerType === "live") {
              refUrlLive.current = archiveUrl;
            }

            dispatch(setCurrentArchive(archive));
            dispatch(setPlayerType("archive"));
            setUrl(archiveUrl);

            setPipMode(false);
            setPlayerPosition();
          }

          setShowCalendar(false);
          dispatch(setCtrl("live"));
          showControl();
        }}
        isActive={showCalendar}
        currentChannel={currentChannel}
      />
    </>
  );
});
